import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Card } from './ui'

const ActivityFeed = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchActivityData = async () => {
      if (!user) return

      setLoading(true)
      try {
        // Fetch activity data from backend API endpoint
        const response = await fetch('/api/activity-feed')

        if (!response.ok) {
          throw new Error(`Error fetching activity feed: ${response.status}`)
        }

        const data = await response.json()
        // Filter out rating events with less than 4 stars
        const filteredData = data.filter(
          (activity) =>
            activity.type !== 'rated' ||
            (activity.type === 'rated' && activity.rating >= 4)
        )
        setActivities(filteredData)
      } catch (error) {
        console.error('Error fetching activity data:', error)
        setActivities([])
      } finally {
        setLoading(false)
      }
    }

    fetchActivityData()
  }, [user])

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMs = now - date
    const diffInSecs = Math.floor(diffInMs / 1000)
    const diffInMins = Math.floor(diffInSecs / 60)
    const diffInHours = Math.floor(diffInMins / 60)
    const diffInDays = Math.floor(diffInHours / 24)

    if (diffInSecs < 60) return 'just now'
    if (diffInMins < 60) return `${diffInMins}m ago`
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInDays < 7) return `${diffInDays}d ago`

    return date.toLocaleDateString()
  }

  // Get activity verb based on activity type
  const getActivityVerb = (type) => {
    switch (type) {
      case 'created_recipe':
        return 'created 🧑‍🍳'
      case 'favorited':
        return 'favorited ❤️'
      case 'rated':
        return 'rated'
      case 'commented':
        return 'commented 💬'
      default:
        return 'interacted with'
    }
  }

  const navigateToRecipe = (recipeId) => {
    navigate(`/recipe/${recipeId}`)
  }

  if (!user) {
    return (
      <div className="container mx-auto max-w-3xl px-2 sm:px-4 py-4 sm:py-6 mt-4">
        <Card className="p-4 sm:p-6 text-center">
          <h1 className="text-xl sm:text-2xl font-bold mb-4">Activity Feed</h1>
          <p className="text-gray-600">Please sign in to view activity.</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-3xl px-0 sm:px-4 py-0 sm:py-6 mt-4">
      <Card className="p-0 sm:p-6 shadow-none sm:shadow">
        <h1 className="text-xl font-bold p-4 sm:p-0 sm:text-2xl sm:mb-6 border-b sm:border-0">
          Activity Feed
        </h1>

        {loading ? (
          <div className="py-8 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-500 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading activity...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="p-4 text-center">
            <p className="text-gray-600">No recent activity found.</p>
            <p className="text-gray-500 mt-2">
              Activity will appear here as users interact with recipes!
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {activities
              .sort((a, b) => {
                // Sort by date (most recent first)
                return new Date(b.date) - new Date(a.date)
              })
              .map((activity, index) => {
                const isCurrentUser = user && activity.userId === user.sub
                const isComment = activity.type === 'commented'
                const isNewRecipe = activity.type === 'created_recipe'

                return (
                  <div
                    key={`${activity.type}-${activity.recipeId}-${index}`}
                    className={`cursor-pointer px-4 py-3 sm:py-4 sm:rounded-lg sm:hover:bg-gray-50 sm:my-2 ${isNewRecipe ? 'bg-green-50' : ''}`}
                    onClick={() => navigateToRecipe(activity.recipeId)}
                  >
                    {/* User and action header */}
                    <div className="flex items-start mb-3">
                      {/* User avatar */}
                      {activity.userImage ? (
                        <img
                          src={activity.userImage}
                          alt={activity.userName}
                          className={`rounded-full mr-3 flex-shrink-0 ${isNewRecipe ? 'w-12 h-12 border-2 border-green-200' : 'w-10 h-10'}`}
                        />
                      ) : (
                        <div
                          className={`rounded-full bg-gray-200 mr-3 flex items-center justify-center flex-shrink-0 ${isNewRecipe ? 'w-12 h-12 border-2 border-green-200' : 'w-10 h-10'}`}
                        >
                          <span className="text-sm text-gray-500">
                            {activity.userName?.charAt(0)}
                          </span>
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        {/* User name and action - side by side with wrapping */}
                        <div className="flex flex-wrap items-baseline">
                          <span
                            className={`font-medium mr-1 ${isNewRecipe ? 'text-lg' : ''}`}
                          >
                            {isCurrentUser ? 'You' : activity.userName}
                          </span>
                          <span
                            className={`${isNewRecipe ? 'text-gray-700 font-medium' : 'text-gray-500'}`}
                          >
                            {getActivityVerb(activity.type)}{' '}
                            {activity.type === 'rated'
                              ? `${activity.rating}/5`
                              : ''}
                          </span>
                        </div>

                        <span className="text-gray-400 text-sm mt-1 block">
                          {formatDate(activity.date)}
                        </span>
                      </div>

                      {/* Options button - hidden for now */}
                      <button className="text-gray-500 p-1 opacity-40">
                        <span className="text-xl">···</span>
                      </button>
                    </div>

                    {/* Recipe information */}
                    <div
                      className={`flex ${isNewRecipe ? 'flex-col sm:flex-row' : 'items-center'}`}
                    >
                      {/* Recipe image - for new recipes, show larger image first */}
                      {isNewRecipe && activity.recipeImage ? (
                        <img
                          src={activity.recipeImage}
                          alt={activity.recipeName}
                          className="w-full h-40 sm:w-48 sm:h-36 object-cover rounded-md mb-3 sm:mb-0 sm:mr-4"
                        />
                      ) : null}

                      {/* Recipe content */}
                      <div className="flex-1 min-w-0">
                        <h3
                          className={`${isNewRecipe ? 'font-semibold text-lg' : 'font-medium'}`}
                        >
                          {activity.recipeName}
                          {isNewRecipe && (
                            <span className="ml-2 inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                              New Recipe
                            </span>
                          )}
                        </h3>

                        {/* Show recipe description for new recipes */}
                        {isNewRecipe && activity.recipeDescription && (
                          <p className="text-gray-600 mt-2 line-clamp-2">
                            {activity.recipeDescription}
                          </p>
                        )}

                        {/* Show comment text for comments */}
                        {isComment && activity.comment && (
                          <p className="text-gray-600 mt-1 text-sm italic">
                            "{activity.comment}"
                          </p>
                        )}
                      </div>

                      {/* Recipe image - for other activities show small thumbnail */}
                      {!isNewRecipe && activity.recipeImage ? (
                        <img
                          src={activity.recipeImage}
                          alt={activity.recipeName}
                          className="w-16 h-16 object-cover rounded-md ml-3 flex-shrink-0"
                        />
                      ) : null}
                    </div>
                  </div>
                )
              })}
          </div>
        )}
      </Card>
    </div>
  )
}

export default ActivityFeed
